import React, { useMemo, useState } from 'react';
import { Popover, PopoverContent, PopoverTrigger } from './ui/popover';
import { Button } from './ui/button';
import { Search, ChevronsUpDown, Check, X } from 'lucide-react';

/**
 * MultiSelectCombobox — a dynamic-search multi-select popover with a
 * Select-All / Clear-All toggle. Used by the Pay Distribution portal for
 * employee and salary-component pickers.
 *
 * Props:
 *  - options: [{ value, label, sublabel? }]
 *  - selected: Array<string> — currently selected values
 *  - onChange: (nextSelectedValues: string[]) => void
 *  - placeholder?: string
 *  - searchPlaceholder?: string
 *  - emptyText?: string
 *  - triggerTestId?: string
 *  - width?: string  (Tailwind class, e.g. "w-full")
 *  - maxHeight?: string (Tailwind class, defaults to max-h-[320px])
 */
const MultiSelectCombobox = ({
  options = [],
  selected = [],
  onChange,
  placeholder = 'Select…',
  searchPlaceholder = 'Search…',
  emptyText = 'No matches.',
  triggerTestId,
  width = 'w-full',
  maxHeight = 'max-h-[320px]',
}) => {
  const [open, setOpen] = useState(false);
  const [q, setQ] = useState('');

  const selectedSet = useMemo(() => new Set(selected), [selected]);

  const filtered = useMemo(() => {
    const query = q.trim().toLowerCase();
    if (!query) return options;
    return options.filter((o) =>
      (o.label || '').toLowerCase().includes(query) ||
      (o.sublabel || '').toLowerCase().includes(query) ||
      (o.value || '').toLowerCase().includes(query),
    );
  }, [q, options]);

  const allFilteredSelected = filtered.length > 0 && filtered.every((o) => selectedSet.has(o.value));

  const toggleOne = (v) => {
    const next = new Set(selectedSet);
    if (next.has(v)) next.delete(v); else next.add(v);
    onChange(Array.from(next));
  };

  const toggleAllFiltered = () => {
    const next = new Set(selectedSet);
    if (allFilteredSelected) {
      filtered.forEach((o) => next.delete(o.value));
    } else {
      filtered.forEach((o) => next.add(o.value));
    }
    onChange(Array.from(next));
  };

  const clearAll = () => onChange([]);

  const triggerLabel = selected.length === 0
    ? placeholder
    : selected.length === options.length && options.length > 0
      ? `All ${options.length} selected`
      : `${selected.length} selected`;

  return (
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverTrigger asChild>
        <Button
          type="button"
          variant="outline"
          role="combobox"
          className={`${width} justify-between font-normal`}
          data-testid={triggerTestId}
        >
          <span className="truncate text-left">{triggerLabel}</span>
          <ChevronsUpDown className="w-4 h-4 opacity-60 flex-shrink-0 ml-2" />
        </Button>
      </PopoverTrigger>
      <PopoverContent
        className="p-0 w-[--radix-popover-trigger-width] max-h-[420px] overflow-hidden"
        align="start"
      >
        <div className="flex items-center border-b px-3">
          <Search className="w-4 h-4 opacity-60 mr-2 flex-shrink-0" />
          <input
            autoFocus
            value={q}
            onChange={(e) => setQ(e.target.value)}
            placeholder={searchPlaceholder}
            className="flex-1 py-2 outline-none text-sm bg-transparent"
            data-testid={triggerTestId ? `${triggerTestId}-search` : undefined}
          />
          {selected.length > 0 && (
            <button
              type="button"
              onClick={clearAll}
              className="text-xs text-slate-500 hover:text-slate-700 flex items-center gap-0.5"
              data-testid={triggerTestId ? `${triggerTestId}-clear` : undefined}
              title="Clear all"
            >
              <X className="w-3 h-3" /> Clear
            </button>
          )}
        </div>
        <div className="px-3 py-1.5 border-b bg-slate-50 flex items-center justify-between text-xs text-slate-600">
          <span>
            Showing {filtered.length} of {options.length}
            {selected.length > 0 && ` · ${selected.length} selected`}
          </span>
          <button
            type="button"
            onClick={toggleAllFiltered}
            disabled={filtered.length === 0}
            className="font-medium text-indigo-600 hover:text-indigo-700 disabled:text-slate-300"
            data-testid={triggerTestId ? `${triggerTestId}-select-all` : undefined}
          >
            {allFilteredSelected ? 'Unselect all' : 'Select all'}
          </button>
        </div>
        <div className={`overflow-y-auto ${maxHeight}`}>
          {filtered.length === 0 ? (
            <div className="p-4 text-sm text-slate-500">{emptyText}</div>
          ) : (
            filtered.map((opt) => {
              const on = selectedSet.has(opt.value);
              return (
                <button
                  type="button"
                  key={opt.value}
                  onClick={() => toggleOne(opt.value)}
                  className={`w-full flex items-start gap-3 px-3 py-2 hover:bg-slate-50 border-b last:border-b-0 text-left ${on ? 'bg-indigo-50/60' : ''}`}
                  data-testid={triggerTestId ? `${triggerTestId}-option-${opt.value}` : undefined}
                >
                  <Check className={`w-4 h-4 mt-0.5 flex-shrink-0 ${on ? 'text-indigo-600' : 'text-transparent'}`} />
                  <div className="min-w-0 flex-1">
                    <div className="text-sm font-medium text-slate-900 truncate">{opt.label}</div>
                    {opt.sublabel && (
                      <div className="text-xs text-slate-500 truncate">{opt.sublabel}</div>
                    )}
                  </div>
                </button>
              );
            })
          )}
        </div>
      </PopoverContent>
    </Popover>
  );
};

export default MultiSelectCombobox;
